import type { StagedEntity } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import { EJsonEntityType } from "@kuika/kuika-cl-model/src/enums/entity-type";
import React, { useCallback, useMemo, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { EntityDiffFieldRow } from "./entity-diff-field";
import type { FieldResolution } from "./entity-diff-types";
import { extractMergeDiffFields, extractStagedDiffFields, getEntityDiffConfig } from "./entity-field-extractors";
import "./entity-diff.scss";

// ── Staged mode ──

interface EntityDiffCardStagedProps {
  mode: "staged";
  entityType: number;
  beforeSnapshot?: unknown;
  afterSnapshot?: unknown;
  allStagedEntities?: StagedEntity[];
}

// ── Merge mode ──

interface EntityDiffCardMergeProps {
  mode: "merge";
  entityType: number;
  baseSnapshot?: unknown;
  oursSnapshot?: unknown;
  theirsSnapshot?: unknown;
  allStagedEntities?: StagedEntity[];
  onResolutionChange?: (resolved: boolean, resolvedContent?: string) => void;
  /** Branch names so the whole/bulk/field controls read in source/target terms with the branch. */
  targetLabel?: string;
  sourceLabel?: string;
}

type EntityDiffCardProps = EntityDiffCardStagedProps | EntityDiffCardMergeProps;

export function EntityDiffCard(props: EntityDiffCardProps): React.ReactElement | null {
  const { entityType, mode } = props;
  if (entityType === EJsonEntityType.KScreenDesign) return null;

  if (mode === "staged") {
    return <StagedDiffCard {...props} />;
  }
  return <MergeDiffCard {...props} />;
}

// ── Staged diff card ──

function StagedDiffCard({
  entityType,
  beforeSnapshot,
  afterSnapshot,
  allStagedEntities
}: EntityDiffCardStagedProps): React.ReactElement | null {
  const result = useMemo(
    () => extractStagedDiffFields(entityType, beforeSnapshot, afterSnapshot, allStagedEntities),
    [entityType, beforeSnapshot, afterSnapshot, allStagedEntities]
  );
  // Hooks run before the early return: a snapshot pair that stops or starts producing a diff
  // would otherwise change the hook count between renders and throw.
  const [showUnchanged, setShowUnchanged] = useState(false);

  if (!result) return null;
  const { entity, fields } = result;
  const changedFields = fields.filter((f) => f.changeStatus !== "unchanged");
  const unchangedFields = fields.filter((f) => f.changeStatus === "unchanged");

  return (
    <div className="kuika-entity-diff__card">
      <EntityHeader entity={entity} />
      <div className="kuika-entity-diff__grid-head">
        <span>Field</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {changedFields.map((field) => (
        <EntityDiffFieldRow key={field.key} field={field} mode="staged" />
      ))}
      {unchangedFields.length > 0 && (
        <div className="kuika-entity-diff__show-more-wrap">
          <KuikaButton type="text" size="sm" onClick={() => setShowUnchanged(!showUnchanged)}>
            {showUnchanged ? "Hide" : "Show"} {unchangedFields.length} unchanged field(s)
          </KuikaButton>
          {showUnchanged &&
            unchangedFields.map((field) => <EntityDiffFieldRow key={field.key} field={field} mode="staged" />)}
        </div>
      )}
      {fields.length === 0 && <div className="kuika-entity-diff__empty">No field details available</div>}
    </div>
  );
}

// ── Merge diff card ──

function MergeDiffCard({
  entityType,
  baseSnapshot,
  oursSnapshot,
  theirsSnapshot,
  allStagedEntities,
  onResolutionChange,
  targetLabel,
  sourceLabel
}: EntityDiffCardMergeProps): React.ReactElement | null {
  const targetText: string = targetLabel ? ` (${targetLabel})` : "";
  const sourceText: string = sourceLabel ? ` (${sourceLabel})` : "";
  const result = useMemo(
    () => extractMergeDiffFields(entityType, baseSnapshot, oursSnapshot, theirsSnapshot, allStagedEntities),
    [entityType, baseSnapshot, oursSnapshot, theirsSnapshot, allStagedEntities]
  );

  const config = useMemo(() => getEntityDiffConfig(entityType), [entityType]);
  const [resolutions, setResolutions] = useState<FieldResolution[]>([]);
  const [showRawJson, setShowRawJson] = useState(false);

  const conflictFields = useMemo(() => result?.fields.filter((f) => f.changeStatus === "conflict") ?? [], [result]);
  const allResolved = useMemo(
    () =>
      config.resolutionLevel === "whole"
        ? resolutions.length > 0
        : conflictFields.every((f) => resolutions.some((r) => r.fieldKey === f.key)),
    [config, conflictFields, resolutions]
  );

  const handleResolveField = useCallback(
    (fieldKey: string, choice: "ours" | "theirs" | "base") => {
      const newResolutions = resolutions.filter((r) => r.fieldKey !== fieldKey);
      newResolutions.push({ fieldKey, choice });

      // Linked groups: resolve all fields in the same group
      if (config.linkedGroups && result) {
        const field = result.fields.find((f) => f.key === fieldKey);
        if (field?.linkedGroup) {
          const linkedFields = result.fields.filter((f) => f.linkedGroup === field.linkedGroup && f.key !== fieldKey);
          for (const lf of linkedFields) {
            if (!newResolutions.some((r) => r.fieldKey === lf.key)) {
              newResolutions.push({ fieldKey: lf.key, choice });
            }
          }
        }
      }

      setResolutions(newResolutions);

      // Notify parent
      const nowAllResolved =
        config.resolutionLevel === "whole"
          ? true
          : conflictFields.every((f) => newResolutions.some((r) => r.fieldKey === f.key));

      if (onResolutionChange) {
        const resolvedContent = nowAllResolved
          ? buildResolvedContent(choice, oursSnapshot, theirsSnapshot, baseSnapshot)
          : undefined;
        onResolutionChange(nowAllResolved, resolvedContent);
      }
    },
    [resolutions, config, result, conflictFields, onResolutionChange, oursSnapshot, theirsSnapshot, baseSnapshot]
  );

  const handleWholeEntityResolve = useCallback(
    (choice: "ours" | "theirs") => {
      setResolutions([{ fieldKey: "__whole__", choice }]);
      if (onResolutionChange) {
        const resolvedContent = buildResolvedContent(choice, oursSnapshot, theirsSnapshot, baseSnapshot);
        onResolutionChange(true, resolvedContent);
      }
    },
    [onResolutionChange, oursSnapshot, theirsSnapshot, baseSnapshot]
  );

  if (!result) return null;
  const { entity, fields } = result;
  const wholeResolution = resolutions.find((r) => r.fieldKey === "__whole__");

  return (
    <div className="kuika-entity-diff__card">
      <EntityHeader entity={entity}>
        <KuikaButton type="secondary" size="sm" onClick={() => setShowRawJson(!showRawJson)}>
          {showRawJson ? "Semantic View" : "Raw JSON"}
        </KuikaButton>
      </EntityHeader>

      {showRawJson ? (
        <RawJsonView
          base={baseSnapshot}
          ours={oursSnapshot}
          theirs={theirsSnapshot}
          targetText={targetText}
          sourceText={sourceText}
        />
      ) : (
        <>
          {config.resolutionLevel === "whole" ? (
            <>
              {fields.map((field) => (
                <EntityDiffFieldRow key={field.key} field={field} mode="merge" />
              ))}
              <div className="kuika-entity-diff__resolve-bar" data-resolved={allResolved || undefined}>
                <KuikaButton
                  type="secondary"
                  size="sm"
                  active={wholeResolution?.choice === "ours"}
                  onClick={() => handleWholeEntityResolve("ours")}
                >
                  Use target{targetText}
                </KuikaButton>
                <KuikaButton
                  type="secondary"
                  size="sm"
                  active={wholeResolution?.choice === "theirs"}
                  onClick={() => handleWholeEntityResolve("theirs")}
                >
                  Use source{sourceText}
                </KuikaButton>
                {allResolved && <span className="kuika-entity-diff__resolved-label">Resolved</span>}
              </div>
            </>
          ) : (
            <>
              <BulkResolveBar
                onResolveAll={handleWholeEntityResolve}
                allResolved={allResolved}
                targetText={targetText}
                sourceText={sourceText}
              />
              {fields.map((field) => (
                <EntityDiffFieldRow
                  key={field.key}
                  field={field}
                  mode="merge"
                  isResolved={resolutions.some((r) => r.fieldKey === field.key)}
                  resolvedChoice={resolutions.find((r) => r.fieldKey === field.key)?.choice}
                  targetLabel={targetLabel}
                  sourceLabel={sourceLabel}
                  onResolve={
                    field.changeStatus === "conflict" ? (choice) => handleResolveField(field.key, choice) : undefined
                  }
                />
              ))}
            </>
          )}
        </>
      )}

      {fields.length === 0 && !showRawJson && (
        <div className="kuika-entity-diff__empty">No field details available</div>
      )}
    </div>
  );
}

// ── Sub-components ──

interface EntityHeaderEntity {
  typeName: string;
  name: string;
  parentInfo?: string;
}

interface EntityHeaderProps {
  entity: EntityHeaderEntity;
  children?: React.ReactNode;
}

function EntityHeader({ entity, children }: EntityHeaderProps): React.ReactElement {
  return (
    <div className="kuika-entity-diff__header">
      <div>
        <span className="kuika-entity-diff__header-name">{entity.name || "Unnamed"}</span>
        <span className="kuika-entity-diff__header-type">{entity.typeName}</span>
        {entity.parentInfo && <span className="kuika-entity-diff__pill">{entity.parentInfo}</span>}
      </div>
      {children}
    </div>
  );
}

interface BulkResolveBarProps {
  onResolveAll: (choice: "ours" | "theirs") => void;
  allResolved: boolean;
  targetText: string;
  sourceText: string;
}

function BulkResolveBar({
  onResolveAll,
  allResolved,
  targetText,
  sourceText
}: BulkResolveBarProps): React.ReactElement {
  return (
    <div className="kuika-entity-diff__bulk-bar" data-resolved={allResolved || undefined}>
      <span className="kuika-entity-diff__bulk-bar-label">
        {allResolved ? "All conflicts resolved" : "Resolve all conflicts:"}
      </span>
      <KuikaButton type="secondary" size="sm" onClick={() => onResolveAll("ours")}>
        Use target for all{targetText}
      </KuikaButton>
      <KuikaButton type="secondary" size="sm" onClick={() => onResolveAll("theirs")}>
        Use source for all{sourceText}
      </KuikaButton>
    </div>
  );
}

interface RawJsonViewProps {
  base?: unknown;
  ours?: unknown;
  theirs?: unknown;
  targetText: string;
  sourceText: string;
}

interface RawJsonColumn {
  label: string;
  data: unknown;
}

function RawJsonView({ base, ours, theirs, targetText, sourceText }: RawJsonViewProps): React.ReactElement {
  const format = (v: unknown): string => {
    if (!v) return "";
    if (typeof v === "string") {
      try {
        return JSON.stringify(JSON.parse(v), null, 2);
      } catch {
        return v;
      }
    }
    return JSON.stringify(v, null, 2);
  };

  const columns: RawJsonColumn[] = [
    { label: "Base", data: base },
    { label: `Target${targetText}`, data: ours },
    { label: `Source${sourceText}`, data: theirs }
  ];

  return (
    <div className="kuika-entity-diff__raw-json">
      {columns.map(({ label, data }) => (
        <div key={label} className="kuika-entity-diff__raw-json-col">
          <div className="kuika-entity-diff__raw-json-col-label">{label}</div>
          <pre className="kuika-entity-diff__raw-json-pre">{format(data) || "No data"}</pre>
        </div>
      ))}
    </div>
  );
}

function buildResolvedContent(
  choice: "ours" | "theirs" | "base",
  ours?: unknown,
  theirs?: unknown,
  base?: unknown
): string {
  const source = choice === "ours" ? ours : choice === "theirs" ? theirs : base;
  if (!source) return "{}";
  if (typeof source === "string") return source;
  return JSON.stringify(source);
}
